import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import BreadCrumbComp from "../components/BreadCrumbComp";
import RenderToast from "../components/RenderToast";
import API from "../utils/api";
import classes from "./ContactUs.module.css";
import { GoCheckCircleFill } from "react-icons/go";
import Section2 from "./Section2";
import PageSchema from "../components/PageSchema";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function ContactUs() {
  const location = useLocation();

  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [skuDisabled, setSkuDisabled] = useState(false);

  useEffect(() => {
    if (location.state?.sku) {
      setFormData((prev) => ({ ...prev, sku: location.state.sku }));
      setSkuDisabled(true);
    }
  }, [location.state]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { sku, name, email, phone, address, message } = formData;

    if (!sku) return RenderToast({ type: "error", message: "SKU is required" });
    if (!name)
      return RenderToast({ type: "error", message: "Name is required" });
    if (!email)
      return RenderToast({ type: "error", message: "Email is required" });
    if (!phone)
      return RenderToast({ type: "error", message: "Phone is required" });
    if (!message)
      return RenderToast({ type: "error", message: "Message is required" });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email))
      return RenderToast({ type: "error", message: "Invalid email format" });

    setLoading(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        { sku, name, email, phone, address, message },
        { publicKey: EMAILJS_PUBLIC_KEY },
      );

      RenderToast({ type: "success", message: "Message sent successfully!" });
      setFormData({
        sku: skuDisabled ? sku : "",
        name: "",
        email: "",
        phone: "",
        address: "",
        message: "",
      });

      // Best-effort log to the admin dashboard DB; email delivery above doesn't depend on this.
      const logData = new FormData();
      logData.append("sku", sku);
      logData.append("name", name);
      logData.append("email", email);
      logData.append("phone", phone);
      logData.append("address", address);
      logData.append("message", message);
      fetch(`${API.base}/contact.php`, { method: "POST", body: logData }).catch(() => {});
    } catch {
      RenderToast({ type: "error", message: "Server error. Try again later." });
    }

    setLoading(false);
  };

  const leftContentItem = [
    {
      id: 1,
      title: "Comprehensive IT Solutions",
      desc: "Tailored services to optimize your IT infrastructure.",
    },
    {
      id: 2,
      title: "Trusted Quality",
      desc: "Delivering premium IT hardware and networking solutions.",
    },
    {
      id: 3,
      title: "Reliable Support",
      desc: "Offering guidance and technical support for all your IT needs.",
    },
  ];

  return (
    <>
      <PageSchema
        type="WebPage"
        title="Contact Prime Cloud Systems | IT Hardware & Networking Solutions"
        description="Get in touch with Prime Cloud Systems for servers, PCs, networking products, and IT solutions across the USA. Our team is ready to assist with inquiries, orders, and support."
        url="https://primecloudsystems.com/contact-us"
        breadcrumbs={[
          { name: "Home", url: "https://primecloudsystems.com/" },
          {
            name: "Contact Us",
            url: "https://primecloudsystems.com/contact-us",
          },
        ]}
      />

      <BreadCrumbComp
        currentPage="Contact Us"
        title="Prime Cloud Systems – Request a Quote"
        description="Request a quote from Prime Cloud Systems for enterprise IT hardware, networking equipment, and IT solutions. Get fast, accurate pricing and personalized solutions for your business needs."
      />

      <div className={classes.contactPage}>
        {/* Contact Form */}
        <div className={classes.formWrapper}>
          <div className={classes.leftContent}>
            <h3>Talk to our sales team</h3>
            <p>
              Looking to upgrade your IT systems or explore our high-performance
              hardware? Learn how Prime Cloud Systems can support your business.
              We’ll respond promptly.
            </p>

            <div className={classes.checkIconBox}>
              {leftContentItem.map((item) => (
                <div className={classes.checkBoxItem} key={item.id}>
                  <div className={classes.checkBoxIcon}>
                    <GoCheckCircleFill />
                  </div>
                  <div className={classes.checkBoxContent}>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className={classes.trustedHeading}>
              Trusted by businesses across the USA
            </h3>
            <Section2 />
          </div>

          <form className={classes.contactForm} onSubmit={handleSubmit}>
            <h2 className={classes.title}>Get in Touch</h2>
            <p className={classes.subtitle}>
              We will respond as soon as possible.
            </p>

            <div className={classes.row}>
              <div className={classes.col}>
                <input
                  type="text"
                  placeholder="SKU *"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  disabled={skuDisabled}
                />
              </div>

              <div className={classes.col}>
                <input
                  type="text"
                  placeholder="Full Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={classes.row}>
              <div className={classes.col}>
                <input
                  type="email"
                  placeholder="Email *"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className={classes.col}>
                <input
                  type="tel"
                  placeholder="Phone *"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={classes.fullRow}>
              <input
                type="text"
                placeholder="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className={classes.fullRow}>
              <textarea
                placeholder="Message *"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
              ></textarea>
            </div>

            <button
              type="submit"
              className={classes.submitBtn}
              disabled={loading}
            >
              {loading ? "Sending..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
